import type { IClaimAiNote } from '@/types/claim';

// Kept out of the main bundle: react-markdown + remark-gfm add ~46 kB gzip for an admin-only modal.
export const loadAiNoteMarkdown = () => import('@/components/admin/AiNoteMarkdown');

export interface AiNoteSection {
  title: string;
  markdown: string;
}

interface ConvertedInvoice {
  date: string;
  exchange_code: string;
  total_amount: string;
  total_in_usd: string;
}

interface Conversion {
  invoices: ConvertedInvoice[];
  totalInUsd: string;
}

const STEP_TITLES: Record<string, string> = {
  extract_format_invoice: 'Receipt summary',
  convert_currencies: 'Currency conversion',
  verify_invoice_details: 'Category check',
  identify_period_gap: 'Receipt date check',
  check_event_participation: 'Event participation check',
};

// Fiona reports its verdict across these steps; they render as one Result section.
const RESULT_STEPS = new Set([
  'flag_invoice_item',
  'flag_invoice_item_reasoning',
  'flag_invoice_item_status',
]);

const RESULT_LABELS: Record<string, string> = {
  approved: '✅ Approved',
  yellow_flag: '⚠️ Yellow flag',
  red_flag: '🚩 Red flag',
};

// The closing fence is optional: Fiona's prompt does not guarantee one.
const CODE_FENCE = /```[^\n]*\n[\s\S]*?(```|$)/g;
const ISO_DATE = /\b\d{4}-\d{2}-\d{2}\b/g;
const SKIPPED_FOR_CATEGORY = /^Step skipped: category '(.+?)' is not '(.+?)'\.?$/;
const SKIPPED = /^Step skipped: (\w)/;

function sentenceCase(name: string): string {
  const words = name.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Returns a plain-language title for a Fiona step. Unknown steps fall back to sentence case. */
export function aiNoteTitle(step: string): string {
  return STEP_TITLES[step] ?? sentenceCase(step);
}

function parseJsonObject(text: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function isConvertedInvoice(value: unknown): value is ConvertedInvoice {
  if (!value || typeof value !== 'object') return false;
  const invoice = value as Record<string, unknown>;
  return ['date', 'exchange_code', 'total_amount', 'total_in_usd'].every(
    (key) => typeof invoice[key] === 'string',
  );
}

function parseConversion(notes: IClaimAiNote[]): Conversion | null {
  const note = notes.find(({ step }) => step === 'convert_currencies');
  const parsed = note && parseJsonObject(note.comment);
  if (!parsed || !Array.isArray(parsed.invoices)) return null;

  const invoices = parsed.invoices.map((entry: { invoice?: unknown } | null) => entry?.invoice);
  if (!invoices.every(isConvertedInvoice)) return null;

  const totalInUsd = parsed.total_invoice_aggregate_in_usd;
  return { invoices, totalInUsd: typeof totalInUsd === 'string' ? totalInUsd : '' };
}

/** Formats `2026-10-04` as `Oct 4, 2026`; returns other text unchanged. */
function formatDate(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Formats `3.73` in `USD` as `$3.73 USD`. */
function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (amount.trim() === '' || !Number.isFinite(value)) return `${amount} ${currency}`;
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(value);
    return `${formatted} ${currency}`;
  } catch {
    // Not an ISO 4217 code.
    return `${value.toFixed(2)} ${currency}`;
  }
}

const isUsd = (invoice: ConvertedInvoice) => invoice.exchange_code.toUpperCase() === 'USD';

/** Keeps Fiona's text about the receipt and replaces the raw receipt with its date and total. */
function summarizeReceipt(comment: string, conversion: Conversion | null): string {
  const intro = comment.replace(CODE_FENCE, '').trim();
  const purchases = (conversion?.invoices ?? []).map(
    (invoice) =>
      `Purchased on ${formatDate(invoice.date)}. Total paid: ${formatMoney(invoice.total_amount, invoice.exchange_code)}.`,
  );
  const details = purchases.length > 1 ? purchases.map((line) => `- ${line}`) : purchases;
  const summary = [intro, details.join('\n')].filter(Boolean).join('\n\n');
  return summary || 'See the attached receipt.';
}

function summarizeConversion({ invoices, totalInUsd }: Conversion): string {
  // Fiona leaves out receipts it found no exchange rate for.
  if (invoices.length === 0) return 'No exchange rate was found, so nothing was converted to USD.';
  if (invoices.every(isUsd)) {
    return invoices.length > 1
      ? 'No conversion needed. The receipts are already in USD.'
      : 'No conversion needed. The receipt is already in USD.';
  }

  const lines = invoices.map((invoice) => {
    const inUsd = formatMoney(invoice.total_in_usd, 'USD');
    if (isUsd(invoice)) return `${inUsd}, already in USD.`;
    return `${formatMoney(invoice.total_amount, invoice.exchange_code)} converted to ${inUsd}.`;
  });
  if (lines.length === 1) return lines[0];

  const list = invoices.map((invoice, i) => `- ${formatDate(invoice.date)}: ${lines[i]}`);
  const total = totalInUsd ? [`Total: ${formatMoney(totalInUsd, 'USD')}.`] : [];
  return [list.join('\n'), ...total].join('\n\n');
}

/** Lists each value of a JSON note as `Label: value`, so no JSON reaches the screen. */
function describeJson(value: unknown, label: string): string[] {
  if (Array.isArray(value)) return value.flatMap((item) => describeJson(item, label));
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) => describeJson(item, sentenceCase(key)));
  }
  return [`- **${label}:** ${String(value)}`];
}

function describeNote({ step, comment }: IClaimAiNote, conversion: Conversion | null): string {
  if (step === 'extract_format_invoice') return summarizeReceipt(comment, conversion);
  if (step === 'convert_currencies' && conversion) return summarizeConversion(conversion);

  const json = parseJsonObject(comment);
  return json ? describeJson(json, '').join('\n') : comment;
}

/** Merges the verdict steps into one section: the status in the title, the reasons below it. */
function describeResult(resultNotes: IClaimAiNote[]): AiNoteSection {
  const statuses: string[] = [];
  const reasons: string[] = [];
  for (const { comment } of resultNotes) {
    const json = parseJsonObject(comment);
    const text = comment.trim();
    if (json) {
      if (typeof json.status === 'string') statuses.push(json.status);
      if (typeof json.reason === 'string') reasons.push(json.reason);
    } else if (/^\w+$/.test(text)) {
      statuses.push(text);
    } else if (text) {
      reasons.push(text);
    }
  }

  const status = statuses[statuses.length - 1];
  return {
    title: status ? `Result: ${RESULT_LABELS[status] ?? sentenceCase(status)}` : 'Result',
    markdown: [...new Set(reasons)].join('\n\n'),
  };
}

function toPlainLanguage(markdown: string): string {
  return markdown
    .replace(SKIPPED_FOR_CATEGORY, 'Not applicable. This claim is under $1, not $2.')
    .replace(SKIPPED, (_, first: string) => `Not applicable. ${first.toUpperCase()}`)
    .replace(ISO_DATE, formatDate);
}

/**
 * Turns Fiona's notes into titled markdown sections a Finance reviewer can read: no step names,
 * code blocks or JSON, readable dates and amounts, a short receipt summary instead of the scanned
 * receipt, and one Result section with the verdict.
 */
export function toAiNoteSections(notes: IClaimAiNote[]): AiNoteSection[] {
  const conversion = parseConversion(notes);
  const resultNotes = notes.filter(({ step }) => RESULT_STEPS.has(step));
  return notes.flatMap((note) => {
    if (RESULT_STEPS.has(note.step)) {
      if (note !== resultNotes[0]) return [];
      const result = describeResult(resultNotes);
      return [{ ...result, markdown: toPlainLanguage(result.markdown) }];
    }
    return [
      { title: aiNoteTitle(note.step), markdown: toPlainLanguage(describeNote(note, conversion)) },
    ];
  });
}
